import * as React from 'react';
import {
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button, Dialog } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { usePlacementAssignedTargetingsQuery } from '../../hooks';
import type { PlacementTargeting } from '../../model';
import { useTargetingBuilderDeviceTypesQuery } from '../../../../targeting/builder/hooks';
import { TargetingSummaryMenu } from '../../../../targeting/builder/components/TargetingSummaryMenu';

const pageSizes = [5, 10, 25, 50, 100] as const;

export function PlacementTargetingDialog({
  placement,
  onOpenChange,
}: {
  placement: { id: string; name: string };
  onOpenChange: (open: boolean) => void;
}) {
  const [filter, setFilter] = React.useState({ searchTerm: '', page: 0, size: 5 });
  const search = useDebouncedValue(
    filter.searchTerm,
    (searchTerm) => {
      setFilter((current) => ({ ...current, searchTerm, page: 0 }));
    },
    650,
  );
  const query = usePlacementAssignedTargetingsQuery(placement.id, filter);
  const deviceTypesQuery = useTargetingBuilderDeviceTypesQuery();
  const deviceTypeNames = new Map(
    (deviceTypesQuery.data ?? []).map((deviceType) => [deviceType.id, deviceType.name]),
  );
  const columns = buildDashboardDataTableColumns<PlacementTargeting>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.name'),
      headerClassName: 'w-64 pl-4',
      className: 'w-64 pl-4 font-medium',
      value: (targeting) => targeting.name,
    },
    {
      id: 'geoLocations',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('targetingBuilder.geo'),
      headerClassName: 'w-28 text-center',
      className: 'w-28 text-center',
      cell: (targeting) => (
        <TargetingSummaryMenu
          title={dashboardT('targetingBuilder.geo')}
          groups={[
            {
              label: dashboardT('targeting.included'),
              items: targeting.geoLocations
                .filter((location) => !location.excluded)
                .map(resolveLocationLabel),
            },
            {
              label: dashboardT('targeting.excluded'),
              items: targeting.geoLocations
                .filter((location) => location.excluded)
                .map(resolveLocationLabel),
            },
          ]}
        />
      ),
    },
    {
      id: 'deviceTypes',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('targetingBuilder.devices'),
      headerClassName: 'w-28 text-center',
      className: 'w-28 text-center',
      cell: (targeting) => (
        <TargetingSummaryMenu
          title={dashboardT('targetingBuilder.devices')}
          items={targeting.deviceTypes.map(
            (deviceType) => deviceTypeNames.get(deviceType.deviceTypeId) ?? deviceType.deviceTypeId,
          )}
        />
      ),
    },
  ]);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DashboardTableDialogContent>
        <DashboardTableDialogHeader
          title={dashboardT('placements.targetingFor', { name: placement.name })}
        />
        <DashboardTableDialogBody>
          <DashboardTableDialogDataTable
            tableLabel={dashboardT('placements.targetingFor', { name: placement.name })}
            columns={columns}
            items={query.data?.content ?? []}
            getRowKey={(targeting) => targeting.id}
            loading={query.isLoading}
            loadingMessage={dashboardT('placements.targetingLoading')}
            errorMessage={query.isError ? dashboardT('placements.targetingLoadError') : undefined}
            emptyMessage={dashboardT('placements.targetingEmpty')}
            minWidthClassName="min-w-[560px]"
            toolbar={
              <DashboardTableSearchInput
                aria-label={dashboardT('placements.searchTargeting')}
                placeholder={dashboardT('placements.searchTargeting')}
                value={search.draft}
                onChange={(event) => {
                  search.setDraft(event.target.value);
                }}
              />
            }
            pagination={{
              value: {
                page: filter.page,
                pageSize: filter.size,
                totalItems: query.data?.totalElements ?? 0,
              },
              pageSizes,
              disabled: query.isFetching,
              onChange: ({ page, pageSize }) => {
                setFilter((current) => ({
                  ...current,
                  page: pageSize === current.size ? page : 0,
                  size: pageSize,
                }));
              },
            }}
          />
        </DashboardTableDialogBody>
        <DashboardTableDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {dashboardT('placements.close')}
          </Button>
        </DashboardTableDialogFooter>
      </DashboardTableDialogContent>
    </Dialog>
  );
}

function resolveLocationLabel(location: PlacementTargeting['geoLocations'][number]) {
  return location.locationName ?? location.countryName ?? location.geocodeId ?? '-';
}
